'use client'

import React from 'react'
import { Bar, BarChart as RechartsBarChart, Cell, Legend, Pie, PieChart as RechartsPieChart, ResponsiveContainer, XAxis, YAxis } from 'recharts'

interface ChartContainerProps {
  children: React.ReactNode
  config: Record<string, { label: string; color: string }>
  className?: string
}

export function ChartContainer({ children, config, className }: ChartContainerProps) {
  return (
    <div className={className}>
      <ResponsiveContainer width="100%" height="100%">
        {React.Children.only(children) as React.ReactElement}
      </ResponsiveContainer>
    </div>
  )
}

interface BarChartProps {
  data: Array<Record<string, any>>
  xAxisDataKey: string
  barDataKey: string
  accessibilityLayer?: boolean
}

export function BarChart({ data, xAxisDataKey, barDataKey, accessibilityLayer }: BarChartProps) {
  return (
    <ResponsiveContainer width="100%" height={400}>
      <RechartsBarChart data={data}>
        <XAxis dataKey={xAxisDataKey} />
        <YAxis />
        <Bar dataKey={barDataKey} fill="hsl(var(--chart-1))" />
        {accessibilityLayer && <text x={0} y={0} className="sr-only">Bar chart showing {barDataKey} by {xAxisDataKey}</text>}
      </RechartsBarChart>
    </ResponsiveContainer>
  )
}

BarChart.XAxis = XAxis
BarChart.YAxis = YAxis
BarChart.Bar = Bar

interface PieChartProps {
  data: Array<Record<string, any>>
  dataKey: string
  nameKey: string
  accessibilityLayer?: boolean
}

export function PieChart({ data, dataKey, nameKey, accessibilityLayer }: PieChartProps) {
  return (
    <ResponsiveContainer width="100%" height={400}>
      <RechartsPieChart>
        <Pie
          data={data}
          dataKey={dataKey}
          nameKey={nameKey}
          cx="50%"
          cy="50%"
          outerRadius={80}
          label={(entry) => entry[nameKey]}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={`hsl(var(--chart-${index + 1}))`} />
          ))}
        </Pie>
        <Legend />
        {accessibilityLayer && <text x={0} y={0} className="sr-only">Pie chart showing distribution of {dataKey} by {nameKey}</text>}
      </RechartsPieChart>
    </ResponsiveContainer>
  )
}

PieChart.Pie = Pie
PieChart.Legend = Legend

